import React, { useCallback, useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader } from "@/components/layouts/PageHeader";
import { Badge } from "@/components/ui/badge";
import {
  Mail,
  Send,
  CheckCircle2,
  XCircle,
  Loader2,
  HeartHandshake,
  ShieldAlert,
  UserCheck,
  Clock,
  KeyRound,
  BellRing,
  RefreshCw,
  Power,
  PowerOff,
  ShieldCheck,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { fetchStageReminderStatus, setStageReminderEnabled, type StageReminderStatus } from "@/api/stageReminders";

type EmailType = "partner_notification" | "admin_unassigned" | "lead_confirmation" | "stage_reminder" | "partner_access";
type SendState = "idle" | "sending" | "success" | "error";

interface EmailTemplate {
  type: EmailType;
  title: string;
  description: string;
  recipient: string;
  icon: React.ReactNode;
  color: string;
  bg: string;
  badgeColor: string;
  subject: string;
}

const TEMPLATES: EmailTemplate[] = [
  {
    type: "partner_notification",
    title: "Ειδοποίηση Συνεργάτη",
    description: "Στέλνεται στον συνεργάτη όταν του ανατίθεται νέο lead. Περιέχει στοιχεία πελάτη και κουμπί για προβολή.",
    recipient: "Συνεργάτης",
    icon: <HeartHandshake className="h-5 w-5" />,
    color: "text-blue-600",
    bg: "bg-blue-50",
    badgeColor: "bg-blue-100 text-blue-700",
    subject: "🔔 Νέα Επαφή Ενδιαφερομένου — Άμεση Ενέργεια",
  },
  {
    type: "admin_unassigned",
    title: "Αχανάθετο Lead (Admin)",
    description: "Στέλνεται στον admin όταν ένα lead δεν μπορεί να ανατεθεί αυτόματα σε συνεργάτη.",
    recipient: "Admin",
    icon: <ShieldAlert className="h-5 w-5" />,
    color: "text-orange-600",
    bg: "bg-orange-50",
    badgeColor: "bg-orange-100 text-orange-700",
    subject: "⚠️ Μη Ανατεθειμένη Επαφή — Απαιτείται Ενέργεια",
  },
  {
    type: "lead_confirmation",
    title: "Επιβεβαίωση στον Πελάτη",
    description: "Branded email Διαθερμική που στέλνεται στον πελάτη μόλις γίνει η ανάθεση. Περιέχει κουμπί για το mini landing page του συνεργάτη.",
    recipient: "Πελάτης (Lead)",
    icon: <UserCheck className="h-5 w-5" />,
    color: "text-emerald-600",
    bg: "bg-emerald-50",
    badgeColor: "bg-emerald-100 text-emerald-700",
    subject: "✅ Λάβαμε το αίτημά σας — Διαθερμική",
  },
  {
    type: "stage_reminder",
    title: "Φιλικό Stage Reminder",
    description: "Ημερήσιο digest ανά συνεργάτη με exact-lead links χωρίς login και προαιρετική ενεργοποίηση dashboard.",
    recipient: "Συνεργάτης",
    icon: <Clock className="h-5 w-5" />,
    color: "text-amber-600",
    bg: "bg-amber-50",
    badgeColor: "bg-amber-100 text-amber-700",
    subject: "Μια μικρή ενημέρωση για 2 επαφές σας | Διαθερμική",
  },
  {
    type: "partner_access",
    title: "Ενεργοποίηση Dashboard",
    description: "Ο προσωπικός, single-use σύνδεσμος 30 λεπτών για δημιουργία κωδικού στο partner dashboard.",
    recipient: "Συνεργάτης",
    icon: <KeyRound className="h-5 w-5" />,
    color: "text-violet-600",
    bg: "bg-violet-50",
    badgeColor: "bg-violet-100 text-violet-700",
    subject: "Ενεργοποίηση dashboard συνεργάτη | Διαθερμική",
  },
];

const EmailTestPage = () => {
  const { toast } = useToast();
  const [recipientEmail, setRecipientEmail] = useState("");
  const [sendStates, setSendStates] = useState<Record<EmailType, SendState>>({
    partner_notification: "idle",
    admin_unassigned: "idle",
    lead_confirmation: "idle",
    stage_reminder: "idle",
    partner_access: "idle",
  });
  const [reminderTests, setReminderTests] = useState({ stageReminder: false, partnerAccess: false });
  const [reminders, setReminders] = useState<StageReminderStatus | null>(null);
  const [reminderLoading, setReminderLoading] = useState(true);
  const [reminderSaving, setReminderSaving] = useState(false);

  const loadReminderStatus = useCallback(async () => {
    setReminderLoading(true);
    try {
      setReminders(await fetchStageReminderStatus());
    } catch (error) {
      toast({ title: "Δεν φορτώθηκαν τα reminders", description: error instanceof Error ? error.message : "Δοκιμάστε ξανά.", variant: "destructive" });
    } finally {
      setReminderLoading(false);
    }
  }, [toast]);

  useEffect(() => { void loadReminderStatus(); }, [loadReminderStatus]);

  const sendTest = async (emailType: EmailType) => {
    if (!recipientEmail.trim()) {
      toast({ title: "Εισάγετε email", description: "Συμπληρώστε πρώτα τη διεύθυνση παραλήπτη.", variant: "destructive" });
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recipientEmail)) {
      toast({ title: "Μη έγκυρο email", description: "Ελέγξτε τη μορφή της διεύθυνσης.", variant: "destructive" });
      return;
    }

    setSendStates(prev => ({ ...prev, [emailType]: "sending" }));

    try {
      const csrfToken = sessionStorage.getItem("csrf_token") ?? "";
      const res = await fetch("/api/send-test-email.php", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
        },
        credentials: "include",
        body: JSON.stringify({ emailType, recipientEmail: recipientEmail.trim() }),
      });
      const data = await res.json();

      if (!res.ok || data.error) {
        throw new Error(data.error || "Αποτυχία αποστολής");
      }

      setSendStates(prev => ({ ...prev, [emailType]: "success" }));
      if (emailType === "stage_reminder") {
        setReminderTests(previous => ({ ...previous, stageReminder: true }));
      } else if (emailType === "partner_access") {
        setReminderTests(previous => ({ ...previous, partnerAccess: true }));
      }
      toast({
        title: "Εστάλη! ✅",
        description: `Test email "${TEMPLATES.find(t => t.type === emailType)?.title}" στο ${recipientEmail}`,
      });

      setTimeout(() => {
        setSendStates(prev => ({ ...prev, [emailType]: "idle" }));
      }, 4000);
    } catch (err: unknown) {
      setSendStates(prev => ({ ...prev, [emailType]: "error" }));
      toast({ title: "Σφάλμα", description: err instanceof Error ? err.message : "Άγνωστο σφάλμα", variant: "destructive" });
      setTimeout(() => {
        setSendStates(prev => ({ ...prev, [emailType]: "idle" }));
      }, 4000);
    }
  };

  const allSent = Object.values(sendStates).filter(s => s === "success").length;
  const reminderTemplateTested = reminderTests.stageReminder && reminderTests.partnerAccess;

  const toggleReminders = async () => {
    if (!reminders) return;
    if (!reminders.enabled && !reminderTemplateTested) {
      toast({ title: "Πρώτα κάντε τις δύο δοκιμές", description: "Στείλτε το Stage Reminder και το Dashboard Activation στο δικό σας email πριν την ενεργοποίηση.", variant: "destructive" });
      return;
    }
    if (!reminders.enabled && !window.confirm("Ενεργοποίηση μόνο για leads που θα δημιουργηθούν από τώρα και μετά; Τα παλιά SLA alerts θα εξαιρεθούν.")) return;
    setReminderSaving(true);
    try {
      const next = await setStageReminderEnabled(!reminders.enabled);
      setReminders(current => current ? { ...current, ...next } : next);
      toast({ title: reminders.enabled ? "Τα reminders απενεργοποιήθηκαν" : "Τα reminders ενεργοποιήθηκαν", description: reminders.enabled ? "Ακυρώθηκαν και όσα digest δεν είχαν σταλεί." : "Η ημερομηνία ενεργοποίησης ορίστηκε τώρα. Δεν θα σταλεί ιστορικό backlog." });
    } catch (error) {
      toast({ title: "Η αλλαγή απέτυχε", description: error instanceof Error ? error.message : "Δοκιμάστε ξανά.", variant: "destructive" });
    } finally {
      setReminderSaving(false);
    }
  };

  return (
    <div className="space-y-8 animate-fade-in">
      <PageHeader
        title="Email Studio & Reminders"
        description="Πραγματικές δοκιμές των branded templates και ασφαλής ενεργοποίηση των partner reminders"
        icon={<Mail />}
        iconGradient="blue"
      />

      <Card className="overflow-hidden border-slate-200">
        <CardHeader className="border-b border-slate-100 bg-slate-50/70 pb-4">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <div className="flex items-center gap-2"><BellRing className="h-5 w-5 text-sky-700" /><CardTitle className="text-lg">Αυτόματα reminders συνεργατών</CardTitle></div>
              <CardDescription className="mt-2 max-w-2xl leading-6">Πρώτο φιλικό digest στις 48 ώρες και ένα τελικό στις 5 ημέρες. Ένα email ανά συνεργάτη την ημέρα, μόνο για νέα leads μετά την ενεργοποίηση.</CardDescription>
            </div>
            <Badge className={reminders?.enabled ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-700"}>{reminderLoading ? "Έλεγχος..." : reminders?.enabled ? "Ενεργό" : "Απενεργοποιημένο"}</Badge>
          </div>
        </CardHeader>
        <CardContent className="p-5 sm:p-6">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {[
              ["Υποψήφια leads τώρα", reminders?.eligible_leads ?? 0],
              ["Συνεργάτες τώρα", reminders?.eligible_partners ?? 0],
              ["Digests που στάλθηκαν", reminders?.delivery?.partners_reached ?? 0],
              ["Πρώτες συνδέσεις", reminders?.onboarding?.first_logins ?? 0],
            ].map(([label, value]) => <div key={String(label)} className="rounded-2xl border border-slate-200 bg-white p-4"><p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p><p className="mt-2 text-2xl font-bold tabular-nums text-slate-950">{value}</p></div>)}
          </div>
          <div className="mt-5 flex flex-col gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 sm:flex-row sm:items-center sm:justify-between">
            <p className="flex items-start gap-2 text-sm leading-6 text-emerald-900"><ShieldCheck className="mt-0.5 h-5 w-5 shrink-0" /><span><strong>Backlog protection:</strong> η ενεργοποίηση γράφει νέο cutoff εκείνη τη στιγμή. Τα σημερινά παλιά SLA alerts δεν μπαίνουν στην αποστολή.</span></p>
            <div className="flex shrink-0 gap-2">
              <Button type="button" variant="outline" onClick={() => void loadReminderStatus()} disabled={reminderLoading} className="h-11"><RefreshCw className={`mr-2 h-4 w-4 ${reminderLoading ? "animate-spin" : ""}`} /> Ανανέωση</Button>
              <Button type="button" onClick={() => void toggleReminders()} disabled={!reminders || reminderSaving} className={`h-11 ${reminders?.enabled ? "bg-rose-600 hover:bg-rose-700" : "bg-emerald-600 hover:bg-emerald-700"}`}>{reminderSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : reminders?.enabled ? <PowerOff className="mr-2 h-4 w-4" /> : <Power className="mr-2 h-4 w-4" />}{reminders?.enabled ? "Απενεργοποίηση" : "Ενεργοποίηση"}</Button>
            </div>
          </div>
          {!reminders?.enabled && <p className="mt-3 text-xs text-slate-500">Για ενεργοποίηση πρέπει στην ίδια συνεδρία να έχουν φτάσει επιτυχώς τα test emails «Stage Reminder» και «Ενεργοποίηση Dashboard».</p>}
        </CardContent>
      </Card>

      {/* Recipient input */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Mail className="h-4 w-4 text-blue-500" />
            Διεύθυνση Παραλήπτη
          </CardTitle>
          <CardDescription>
            Όλα τα test emails θα σταλούν στο παρακάτω email — χρησιμοποιήστε τη δική σας διεύθυνση
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-3 max-w-md">
            <div className="flex-1">
              <Label htmlFor="recipient" className="sr-only">Email παραλήπτη</Label>
              <Input
                id="recipient"
                type="email"
                placeholder="yourname@example.com"
                value={recipientEmail}
                onChange={e => setRecipientEmail(e.target.value)}
                className="border-slate-200"
              />
            </div>
          </div>
          {allSent > 0 && (
            <p className="mt-2 text-xs text-emerald-600 flex items-center gap-1">
              <CheckCircle2 className="h-3.5 w-3.5" />
              {allSent} email{allSent > 1 ? "s" : ""} εστάλ{allSent > 1 ? "αν" : "η"} σε αυτή τη συνεδρία
            </p>
          )}
        </CardContent>
      </Card>

      {/* Template cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {TEMPLATES.map(tpl => {
          const state = sendStates[tpl.type];
          return (
            <Card key={tpl.type} className="border-slate-200 flex flex-col">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className={`p-2.5 rounded-xl ${tpl.bg}`}>
                      <span className={tpl.color}>{tpl.icon}</span>
                    </div>
                    <div>
                      <CardTitle className="text-base">{tpl.title}</CardTitle>
                      <span className={`inline-block mt-1 text-xs font-medium px-2 py-0.5 rounded-full ${tpl.badgeColor}`}>
                        Προς: {tpl.recipient}
                      </span>
                    </div>
                  </div>
                  {state === "success" && <CheckCircle2 className="h-5 w-5 text-emerald-500 shrink-0 mt-1" />}
                  {state === "error"   && <XCircle className="h-5 w-5 text-red-500 shrink-0 mt-1" />}
                </div>
              </CardHeader>
              <CardContent className="flex flex-col flex-1 gap-4">
                <p className="text-sm text-slate-500 leading-relaxed">{tpl.description}</p>

                {/* Subject preview */}
                <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
                  <p className="text-xs text-slate-400 mb-0.5 font-medium uppercase tracking-wide">Θέμα email</p>
                  <p className="text-xs text-slate-700 font-medium">{tpl.subject}</p>
                </div>

                {/* Fake data summary */}
                <div className="rounded-lg border border-dashed border-slate-200 bg-white px-3 py-2.5 space-y-1">
                  <p className="text-xs text-slate-400 font-medium uppercase tracking-wide mb-1.5">Δοκιμαστικά δεδομένα</p>
                  <p className="text-xs text-slate-600">👤 <span className="font-medium">Γιώργος Παπαδόπουλος</span></p>
                  <p className="text-xs text-slate-600">📞 <span className="font-medium">6912 345 678</span></p>
                  <p className="text-xs text-slate-600">🛠️ <span className="font-medium">Αντλίες Θερμότητας</span></p>
                  <p className="text-xs text-slate-600">📍 <span className="font-medium">Αθήνα 11526</span></p>
                  {(tpl.type === "partner_notification" || tpl.type === "lead_confirmation" || tpl.type === "stage_reminder" || tpl.type === "partner_access") && (
                    <p className="text-xs text-slate-600">🤝 <span className="font-medium">Νίκος Αλεξίου — Θέρμανση & Κλιματισμός</span></p>
                  )}
                </div>

                <div className="mt-auto">
                  <Button
                    onClick={() => sendTest(tpl.type)}
                    disabled={state === "sending"}
                    className={`w-full gap-2 ${
                      state === "success"
                        ? "bg-emerald-500 hover:bg-emerald-600 text-white"
                        : state === "error"
                        ? "bg-red-500 hover:bg-red-600 text-white"
                        : "bg-slate-800 hover:bg-slate-700 text-white"
                    }`}
                  >
                    {state === "sending" ? (
                      <><Loader2 className="h-4 w-4 animate-spin" /> Αποστολή...</>
                    ) : state === "success" ? (
                      <><CheckCircle2 className="h-4 w-4" /> Εστάλη!</>
                    ) : state === "error" ? (
                      <><XCircle className="h-4 w-4" /> Σφάλμα — Δοκιμάστε ξανά</>
                    ) : (
                      <><Send className="h-4 w-4" /> Στείλε Test Email</>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Info note */}
      <div className="rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 flex items-start gap-3">
        <Mail className="h-4 w-4 text-blue-500 mt-0.5 shrink-0" />
        <p className="text-sm text-blue-700">
          Τα test emails αποστέλλονται με <strong>πλαστά δεδομένα</strong> — οι σύνδεσμοι (mini page, lead view) δεν θα λειτουργούν.
          Σκοπός είναι να ελέγξετε την <strong>εμφάνιση και μορφοποίηση</strong> του κάθε προτύπου.
        </p>
      </div>
    </div>
  );
};

export default EmailTestPage;
